/**
 * API service for communicating with the admin backend Cloud Functions.
 *
 * Every request attaches the Google ID token as a Bearer token in the
 * Authorization header so the API Gateway (or backend fallback) can
 * identify the admin.
 *
 * Path modes (controlled by REACT_APP_API_MODE):
 *   "direct"  → calls Cloud Functions directly at /admin-user-*
 *               (for testing without API Gateway)
 *   "gateway" → calls via API Gateway at /api/user-*
 *               (production, default)
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const API_MODE = process.env.REACT_APP_API_MODE || "gateway";

/**
 * Resolve the endpoint path based on the current API mode.
 *   gateway  → /api/admin/user-list  (admin endpoints behind API Gateway)
 *   direct   → /user-list            (Cloud Functions directly)
 */
function resolvePath(endpoint) {
  if (API_MODE === "direct") {
    return `/${endpoint}`;
  }
  return `/api/admin/${endpoint}`;
}

/**
 * Generic fetch wrapper that attaches the auth token and handles errors.
 */
async function request(path, options = {}, token) {
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const fullUrl = `${API_BASE}${path}`;

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // For non-2xx responses, try to extract the error message
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorBody = await response.json();
      if (errorBody?.error?.message) {
        errorMessage = errorBody.error.message;
      }
    } catch {
      /* response may not be JSON */
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

// ── User management endpoints ───────────────────────────────────────────────

/**
 * Fetch a paginated list of API users.
 * @param {string} token - Google ID token
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of records per page
 */
export async function fetchUsers(token, page = 1, pageSize = 20) {
  const path = resolvePath("user-list");
  return request(
    `${path}?page=${page}&page_size=${pageSize}`,
    { method: "GET" },
    token
  );
}

/**
 * Create a new API user.
 * @param {string} token - Google ID token
 * @param {{ name: string, email: string }} data - User data
 * @returns {Promise<{ user_id: string, name: string, email: string, refresh_token: string, message: string }>}
 */
export async function createUser(token, data) {
  return request(
    resolvePath("user-create"),
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token
  );
}

/**
 * Delete an API user.
 * @param {string} token - Google ID token
 * @param {string} userId - The id of the user to delete
 */
export async function deleteUser(token, userId) {
  return request(
    resolvePath("user-delete"),
    {
      method: "DELETE",
      headers: { "user-id": userId },
    },
    token
  );
}

/**
 * Update an API user's name and email.
 * @param {string} token - Google ID token
 * @param {{ id: string, name: string, email: string }} data - Updated user data
 */
export async function updateUser(token, data) {
  return request(
    resolvePath("user-update"),
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token
  );
}

/**
 * Generate a new refresh token for a user.
 * @param {string} token - Google ID token
 * @param {string} userId - The id of the user
 * @returns {Promise<{ refresh_token: string, message: string }>}
 */
export async function generateNewKey(token, userId) {
  return request(
    resolvePath("user-generate-key"),
    {
      method: "GET",
      headers: { "user-id": userId },
    },
    token
  );
}

// ── Analytics endpoint ───────────────────────────────────────────────────────

/**
 * Fetch API Gateway analytics from the Cloud Monitoring proxy function.
 * @param {string} token - Google ID token
 * @param {string} timeRange - One of "1h", "24h", "7d"
 * @returns {Promise<{
 *   summary: object,
 *   time_series: Array,
 *   by_method: Array,
 *   top_paths: Array,
 *   time_range: string
 * }>}
 */
export async function fetchAnalytics(token, timeRange = "24h") {
  const path = resolvePath("gateway-analytics");
  return request(
    `${path}?range=${timeRange}`,
    { method: "GET" },
    token
  );
}
